import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container, Box, Typography, Paper, Card, CardContent, IconButton, Button, Chip,
    ToggleButton, ToggleButtonGroup, Snackbar, Alert
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { 
    format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, 
    isWithinInterval, parseISO 
} from 'date-fns';
import { vi } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as API from '../api';
import BookingDispatchDialog from './operator/BookingDispatchDialog';
import TourOpsTab from './operator/TourOpsTab';
import TransportOpsTab from './operator/TransportOpsTab';
import HotelOpsTab from './operator/HotelOpsTab';
import ExtraOpsTab from './operator/ExtraOpsTab';
import CustomDatePicker from './CustomDatePicker';

// --- 1. CẤU HÌNH GIAO DIỆN ---
const dashboardTheme = createTheme({
    typography: {
        fontFamily: "'Roboto', 'Arial', sans-serif",
        button: { textTransform: 'none', fontWeight: 'bold' },
        allVariants: { fontFeatureSettings: "'cv11', 'ss01'" }
    },
    shape: { borderRadius: 8 }
});

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

// Các Hằng số dùng cho Lịch Tuần
const OPS_STATUS_COLORS = {
    'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'OPERATING': 'secondary', 'DONE': 'success'
};
const OPS_STATUS_LABELS = {
    'OPEN': 'Chờ ĐH', 'CONTACTED': 'Đã liên hệ', 'CONFIRMED': 'NCC Xác nhận', 'OPERATING': 'Đang đi', 'DONE': 'Đã đi'
};
const BOOKING_CATEGORY_LABELS = {
    'TOUR': 'Tour trọn gói / Ghép', 'TRANSPORT': 'Dịch vụ Thuê xe', 'HOTEL': 'Dịch vụ Phòng KS',
    'FLIGHT': 'Vé máy bay', 'COMBO': 'Combo Dịch vụ', 'OTHER': 'Dịch vụ Khác'
};

function OperatorDashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- STATE QUẢN LÝ TAB & DỮ LIỆU ---
    const viewMode = searchParams.get('view') || 'week';
    const [bookings, setBookings] = useState([]);
    const [transports, setTransports] = useState([]); 
    const [tours, setTours] = useState([]);
    
    // State dùng để reset bộ lọc các tab con khi bấm Làm mới
    const [refreshKey, setRefreshKey] = useState(0); 

    // State Lịch Tuần
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

    // State Thông báo (Toast)
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const [selectedBookingForDispatch, setSelectedBookingForDispatch] = useState(null);

    // --- CÁC HÀM HELPER CHUNG ---
    const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });
    const handleCloseToast = (event, reason) => { if (reason !== 'clickaway') setToast({ ...toast, open: false }); };

    const handleViewChange = (e, newMode) => {
        if (newMode) {
            const currentParams = Object.fromEntries([...searchParams]);
            setSearchParams({ ...currentParams, view: newMode });
        }
    };

    const handleViewBooking = (bookingCode) => {
        navigate('/form', { state: { bookingCodeToLoad: bookingCode } });
    };

    // --- LOAD DỮ LIỆU TỔNG TỪ BACKEND ---
    const loadData = async () => {
        try {
            const [bkRes, tourRes, transportRes] = await Promise.all([
                API.getAllBookings(),
                API.fetchTours(),
                API.client.get('/transports/')
            ]);
            const validBookings = (bkRes.data || []).filter(b => b.booking_status !== 'CANCELLED');
            setBookings(validBookings);
            setTours(tourRes.data);
            
            const transportData = Array.isArray(transportRes.data) ? transportRes.data : (transportRes.data.results || []);
            setTransports(transportData);
        } catch (err) {
            console.error("Lỗi tải dữ liệu:", err);
            showToast("Có lỗi xảy ra khi tải dữ liệu từ máy chủ", "error");
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleRefresh = () => {
        loadData(); 
        setRefreshKey(prevKey => prevKey + 1); 
        if (viewMode === 'week') setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
        showToast('Đã làm mới dữ liệu và bộ lọc', 'info');
    };

    // --- LOGIC LỊCH TUẦN ---
    const weekDays = [];
    let day = currentWeekStart;
    for (let i = 0; i < 7; i++) {
        weekDays.push(day);
        day = addDays(day, 1);
    }

    const handlePrevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
    const handleNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
    const handleToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

    // Nhảy từ Lịch Tuần sang Tab Điều Tour
    const handleJumpToList = (dateObject) => {
        const dateStr = format(dateObject, 'yyyy-MM-dd');
        setSearchParams({ view: 'tour', start: dateStr, end: dateStr, mode: 'CUSTOM' });
    };

    // Thuật toán chia lưới Grid Lịch Tuần
    const weekDaysStr = weekDays.map(d => format(d, 'yyyy-MM-dd'));
    const gridBookings = [];
    const lanes = [];

    const bookingsInWeek = bookings.filter(bk => {
        if (!bk.start_date) return false;
        const bStartStr = bk.start_date.split('T')[0];
        const bEndStr = bk.end_date ? bk.end_date.split('T')[0] : bStartStr;
        return bStartStr <= weekDaysStr[6] && bEndStr >= weekDaysStr[0];
    });

    bookingsInWeek.sort((a, b) => {
        const aStartStr = a.start_date.split('T')[0];
        const bStartStr = b.start_date.split('T')[0];
        if (aStartStr === bStartStr) {
             const aEndStr = a.end_date ? a.end_date.split('T')[0] : aStartStr;
             const bEndStr = b.end_date ? b.end_date.split('T')[0] : bStartStr;
             return new Date(bEndStr).getTime() - new Date(aEndStr).getTime();
        }
        return new Date(aStartStr).getTime() - new Date(bStartStr).getTime();
    });

    bookingsInWeek.forEach(bk => {
        const bStartStr = bk.start_date.split('T')[0];
        const bEndStr = bk.end_date ? bk.end_date.split('T')[0] : bStartStr;

        let startCol = 1;
        for (let i = 0; i < 7; i++) {
            if (bStartStr <= weekDaysStr[i]) { startCol = i + 1; break; }
        }

        let endCol = 7;
        for (let i = 6; i >= 0; i--) {
            if (bEndStr >= weekDaysStr[i]) { endCol = i + 1; break; }
        }

        let laneIdx = -1;
        const bStartTime = new Date(bStartStr).getTime();
        const bEndTime = new Date(bEndStr).getTime();

        for (let i = 0; i < lanes.length; i++) {
            if (lanes[i] < bStartTime) {
                laneIdx = i;
                lanes[i] = bEndTime;
                break;
            }
        }
        if (laneIdx === -1) {
            laneIdx = lanes.length;
            lanes.push(bEndTime); 
        }

        gridBookings.push({
            ...bk,
            gridColStart: startCol,
            gridColEnd: endCol + 1, 
            gridRow: laneIdx + 1
        });
    });

    return (
        <ThemeProvider theme={dashboardTheme}>
            <Container maxWidth={false} sx={{ mt: 3, mb: 3, maxWidth: '1400px' }}>
                
                {/* --- HEADER CHUNG --- */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#007373' }}>
                        ĐIỀU HÀNH TOUR
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewChange}
                            size="small"
                            sx={{ bgcolor: 'white' }}
                        >
                            <ToggleButton value="week" sx={{ px: 3 }}> Lịch tổng quan </ToggleButton>
                            <ToggleButton value="tour" sx={{ px: 3 }}> Điều Tour </ToggleButton>
                            <ToggleButton value="transport" sx={{ px: 3 }}> Điều Xe (Riêng) </ToggleButton>
                            <ToggleButton value="hotel" sx={{ px: 3 }}> Điều KS (Riêng) </ToggleButton>
                            <ToggleButton value="extra" sx={{ px: 3 }}> Điều DV (Riêng) </ToggleButton>
                        </ToggleButtonGroup>

                        <Button 
                            variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}
                            sx={{ bgcolor: 'white', border: '1px solid rgba(0, 0, 0, 0.12)' }}
                        >
                            Làm mới
                        </Button>
                    </Box>
                </Box>

                {/* --- MÀN HÌNH 1: LỊCH TUẦN --- */}
                {viewMode === 'week' && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>              
                        <Paper sx={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 3, paddingRight: 3, marginBottom: 2, borderRadius: 2 }}>
                            {/* Thanh công cụ Điều hướng Lịch */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '12px' }}>
                                    <IconButton size="small" onClick={handlePrevWeek} sx={{ border: '1px solid #ddd', borderRadius: 1, height: '40px', width: '40px', bgcolor: 'white', '&:hover': { bgcolor: '#f5f5f5' }}}>
                                        <ArrowBackIosNewIcon fontSize="small" />
                                    </IconButton>
                                    
                                    {(() => {
                                        const today = new Date();
                                        const isCurrentWeek = isWithinInterval(today, { start: currentWeekStart, end: endOfWeek(currentWeekStart, { weekStartsOn: 1 }) });
                                        return (
                                            <Button variant="outlined" size="small" onClick={handleToday} disabled={isCurrentWeek}
                                                sx={{ height: '40px', px: 3, borderRadius: 1, ...(isCurrentWeek ? { bgcolor: '#f0f0f0 !important'} : { borderColor: '#ddd', color: '#333', bgcolor: 'white' })}}
                                            >HÔM NAY</Button>
                                        );
                                    })()}

                                    <IconButton size="small" onClick={handleNextWeek} sx={{ border: '1px solid #ddd', borderRadius: 1, height: '40px', width: '40px', bgcolor: 'white', '&:hover': { bgcolor: '#f5f5f5' }}}>
                                        <ArrowForwardIosIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box sx={{ width: '150px' }}>
                                        <CustomLabel title="Từ ngày" />
                                        <CustomDatePicker value={format(currentWeekStart, 'yyyy-MM-dd')} onChange={(e) => { if(e.target.value) setCurrentWeekStart(startOfWeek(parseISO(e.target.value), { weekStartsOn: 1 })); }} />
                                    </Box>
                                    <Box sx={{ width: '150px' }}>
                                        <CustomLabel title="Đến ngày" />
                                        <CustomDatePicker value={format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'yyyy-MM-dd')} onChange={(e) => { if(e.target.value) setCurrentWeekStart(startOfWeek(parseISO(e.target.value), { weekStartsOn: 1 })); }} />
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto', marginBottom: '24px' }}>
                                    {Object.keys(OPS_STATUS_LABELS).map(status => (
                                        <Chip key={status} label={OPS_STATUS_LABELS[status]} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 'bold', bgcolor: 'white', color: theme => theme.palette[OPS_STATUS_COLORS[status]].main, border: '1px solid', borderColor: theme => theme.palette[OPS_STATUS_COLORS[status]].main }} />
                                    ))}
                                </Box>
                            </Box>
                        </Paper>

                        {/* Bảng Lưới Lịch */}
                        <Box sx={{ position: 'relative', height: '65vh', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1, flex: 1, height: '100%' }}>
                                {weekDays.map((dayItem, index) => {
                                    const isToday = format(dayItem, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                                    return (
                                        <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                            <Paper elevation={isToday ? 4 : 1} sx={{ height: '100%', borderTop: isToday ? '4px solid #007373' : '4px solid transparent', bgcolor: isToday ? '#e0f2f1' : '#f9f9f9', display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
                                                <Box onClick={() => handleJumpToList(dayItem)} sx={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid #e0e0e0', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#f0f0f0' } }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: isToday ? '#007373' : '#555', lineHeight: 1.2 }}>
                                                        {format(dayItem, 'EEEE', { locale: vi })}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">{format(dayItem, 'dd/MM')}</Typography>
                                                </Box>
                                                <Box sx={{ flex: 1 }} />
                                            </Paper>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box sx={{ position: 'absolute', top: '64px', left: 0, right: 0, bottom: 0, overflowY: 'auto', overflowX: 'hidden', pt: 1, pb: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', alignItems: 'start' }}>
                                    {gridBookings.map(bk => (
                                        <Card 
                                            key={bk.booking_code} 
                                            onClick={() => setSelectedBookingForDispatch(bk)}
                                            sx={{ 
                                                gridColumn: `${bk.gridColStart} / ${bk.gridColEnd}`, gridRow: bk.gridRow,
                                                ml: '8px', mr: '8px', mb: 1, cursor: 'pointer', zIndex: 10, bgcolor: 'white',
                                                borderLeft: `4px solid`, borderLeftColor: `${OPS_STATUS_COLORS[bk.operation_status || 'OPEN']}.main`,
                                                '&:hover': { boxShadow: 3, filter: 'brightness(0.95)' }, transition: 'transform 0.2s',
                                            }}
                                        >
                                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: '0.75rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {bk.customer_detail ? bk.customer_detail.name : 'Khách'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {(() => {
                                                        const tourInfo = tours.find(t => t.tour_code === bk.tour);
                                                        if (tourInfo) return tourInfo.base_tour_name || tourInfo.name;
                                                        if (bk.tour_name) return bk.tour_name;
                                                        return BOOKING_CATEGORY_LABELS[bk.booking_category] || 'Dịch vụ lẻ';
                                                    })()}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* --- CÁC MÀN HÌNH TAB CHUYÊN BIỆT --- */}
                {viewMode === 'tour' && (
                    <TourOpsTab 
                        key={`tour-${refreshKey}`}
                        bookings={bookings} toursList={tours} 
                        onReload={loadData} handleViewBooking={handleViewBooking} showToast={showToast}
                    />
                )}

                {viewMode === 'transport' && (
                    <TransportOpsTab 
                        key={`transport-${refreshKey}`}
                        transports={transports} 
                        onReload={loadData} handleViewBooking={handleViewBooking} showToast={showToast}
                    />
                )}

                {viewMode === 'hotel' && (
                    <HotelOpsTab 
                        key={`hotel-${refreshKey}`}
                        bookings={bookings} 
                        onReload={loadData} handleViewBooking={handleViewBooking} showToast={showToast}
                    />
                )}

                {viewMode === 'extra' && (
                    <ExtraOpsTab 
                        key={`extra-${refreshKey}`}
                        bookings={bookings} 
                        onReload={loadData} handleViewBooking={handleViewBooking} showToast={showToast}
                    />
                )}

                {/* --- THÔNG BÁO --- */}
                <Snackbar open={toast.open} autoHideDuration={3000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ mt: 8 }}>
                    <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">
                        {toast.message}
                    </Alert>
                </Snackbar>

                <BookingDispatchDialog
                    open={!!selectedBookingForDispatch}
                    onClose={() => setSelectedBookingForDispatch(null)}
                    booking={selectedBookingForDispatch}
                    tours={tours}
                    onViewDetail={handleViewBooking}
                    showToast={showToast}
                />
            </Container>
        </ThemeProvider>
    );
}

export default OperatorDashboard;